import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import './TemplatesPage.css';
import Cookies from "js-cookie";
import { jwtDecode } from "jwt-decode";
import Loader from '../../ui/components/loader/Loader';

const TemplatesPage = () => {
   const navigate = useNavigate();
   const [searchParams] = useSearchParams();
   const selectedSector = searchParams.get("sector") || "";
   const jwt = Cookies.get("jwt");
   const decoded = jwt ? jwtDecode(jwt) : null;
   const gmail = decoded?.email;
   const [loading, setLoading] = useState(true);
   const [formdata, setFormData] = useState(null);
   const [businessData, setBusinessData] = useState(null);

   const fetchUserData = async () => {
      try {
         const url = process.env.REACT_APP_BACKEND_URL;
         // Try to fetch business data first
         const businessResponse = await fetch(`${url}/api/business/email/${gmail}`);
         if (businessResponse.ok) {
            const business = await businessResponse.json();
            setBusinessData(business);
         }

         // Also try old user endpoint for backward compatibility
         const response = await fetch(`${url}/api/user/email/${gmail}`);
         if (response.ok) {
            const data = await response.json();
            data.personalInfo = data.personalInfo || {};
            data.personalInfo.payment = data.personalInfo.payment ?? false;
            data.personalInfo.amount = data.personalInfo.amount ?? 0;
            setFormData(data);
         }
         setLoading(false);
      } catch (error) {
         console.error("Error fetching user data:", error);
         setLoading(false);
      }
   };

   useEffect(() => {
      fetchUserData();
   }, []);

   // Skip payment for now - go directly to form
   const handleTemplates = (templateId) => {
      if (templateId === "jgrpsercstyshh") {
         navigate(`/restaurant-editable/new`);
      } else if (templateId === "food-template-1") {
         navigate(`/food-template-1/new`);
      } else {
         navigate(`/user-details/${templateId}`);
      }
   };

   if (loading) {
      return <Loader />;
   }

   // Get sector-specific templates
   const getSectorTemplates = (sector) => {
      const sectorTemplates = {
         "e-commerce": [
            {
               id: "ecommerce-1",
               title: "Modern Store Template",
               description: "Perfect for e-commerce businesses. Includes product catalog, shopping cart, and checkout features.",
               price: "₹3,099",
               amount: 309900,
               features: ["Product Catalog", "Shopping Cart", "Checkout System", "Inventory Management", "Order Tracking"],
            },
            {
               id: "ecommerce-2",
               title: "Premium E-commerce",
               description: "Advanced e-commerce template with AI recommendations and analytics.",
               price: "₹5,099",
               amount: 509900,
               features: ["Everything in Basic", "AI Recommendations", "Advanced Analytics", "Multi-vendor Support"],
            },
         ],
         "fashion": [
            {
               id: "fashion-1",
               title: "Fashion Boutique",
               description: "Stylish template for fashion brands with lookbook and size guides.",
               price: "₹3,099",
               amount: 309900,
               features: ["Lookbook Gallery", "Size Guides", "Collection Display", "Style Categories"],
            },
         ],
         "automobile": [
            {
               id: "automobile-1",
               title: "Auto Service Center",
               description: "Template for automobile services with booking and vehicle showcase.",
               price: "₹3,099",
               amount: 309900,
               features: ["Service Booking", "Vehicle Showcase", "Service History", "Parts Catalog"],
            },
         ],
         "food": [
            {
               id: "food-template-1",
               title: "Foodie Master (New)",
               description: "Premium Foodie Master template with advanced animations and inline editing.",
               price: "Free",
               amount: 0,
               features: ["Premium Design", "Inline Editing", "Animations", "Hero Slider"],
            },
         ],
         "health": [
            {
               id: "health-1",
               title: "Health & Wellness",
               description: "Template for health services with appointment booking.",
               price: "₹3,099",
               amount: 309900,
               features: ["Appointment Booking", "Practitioner Profiles", "Service Listings"],
            },
         ],
         "entertainment": [
            {
               id: "entertainment-1",
               title: "Event Management",
               description: "Template for entertainment businesses with event listings and ticketing.",
               price: "₹3,099",
               amount: 309900,
               features: ["Event Listings", "Ticket Booking", "Venue Information"],
            },
         ],
         "electronics": [
            {
               id: "electronics-1",
               title: "Tech Store",
               description: "Template for electronics stores with product specs and reviews.",
               price: "₹3,099",
               amount: 309900,
               features: ["Product Specifications", "Comparison Tools", "Tech Reviews"],
            },
         ],
         "finance": [
            {
               id: "finance-1",
               title: "Financial Services",
               description: "Professional template for financial services.",
               price: "₹3,099",
               amount: 309900,
               features: ["Service Listings", "Consultation Booking", "Financial Tools"],
            },
         ],
      };

      return sectorTemplates[sector] || [
         {
            id: "basic-1",
            title: "Basic Template",
            description: "Perfect for small businesses. Includes essential features.",
            price: "₹3,099",
            amount: 309900,
            features: ["Responsive Design", "5 Custom Pages", "Basic SEO Setup", "Contact Form"],
         },
      ];
   };

   const templates = getSectorTemplates(selectedSector || businessData?.businessType);

   if (!selectedSector && !businessData?.businessType) {
      return (
         <div className="templates-page">
            <div className="container">
               <div className="no-sector-selected">
                  <h2>Please select a business sector first</h2>
                  <button onClick={() => navigate("/sector-selection")} className="select-sector-button">
                     Select Sector
                  </button>
               </div>
            </div>
         </div>
      );
   }

   return (
      <div className="templates-page">
         <div className="container">
            <h1 className="page-title">Choose Your {selectedSector || businessData?.businessType} Template</h1>
            <p className="page-subtitle">
               Select from our professionally designed templates for {selectedSector || businessData?.businessType} businesses
            </p>

            <div className="templates-grid">
               {templates.map((template) => (
                  <div key={template.id} className="template-card">
                     <div className="template-header">
                        <h2 className="template-title">{template.title}</h2>
                        <p className="template-price">{template.price}</p>
                     </div>

                     <div className="template-body">
                        <p className="template-description">{template.description}</p>

                        <ul className="template-features">
                           {template.features.map((feature, index) => (
                              <li key={index}>
                                 <i className="fas fa-check-circle"></i>
                                 {feature}
                              </li>
                           ))}
                        </ul>
                     </div>

                     <div className="template-footer">
                        <button
                           className="template-button select-button"
                           onClick={() => handleTemplates(template.id)}
                        >
                           Select Template
                        </button>
                     </div>
                  </div>
               ))}
            </div>
         </div>
      </div>
   );
};

export default TemplatesPage;